import {HStack, VStack} from 'platform/foundation-tw';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {getBrandImageUrl} from 'shared/web';

export interface VehicleIdentityProps {
  /** Raw make code from the Vehicle Catalogue (e.g. `MAKE_SKODA`). Used for logo resolution. */
  readonly make?: string | null;
  /** Localized brand display name (e.g. `Škoda`). */
  readonly makeLabel?: string | null;
  /** Composed model + trim + variant title (e.g. `Superb Laurin & Klement`). */
  readonly modelTitle?: string | null;
}

/**
 * VEHICLEIDENTITY
 * Displays the vehicle brand logo, brand name and model title in the report header.
 *
 * Renders only the parts it can (unsupported makes have no logo, labels hidden
 * when missing). Returns `null` when no identity information is available at all.
 */
export function VehicleIdentity(props: VehicleIdentityProps) {
  const brandImageUrl = getBrandImageUrl(props.make);
  const hasMakeLabel = isNotNilOrEmpty(props.makeLabel);
  const hasModelTitle = isNotNilOrEmpty(props.modelTitle);

  if (isNil(brandImageUrl) && !hasMakeLabel && !hasModelTitle) {
    return null;
  }

  return (
    <HStack spacing={4} align="center">
      {isNotNil(brandImageUrl) ? (
        <img
          src={brandImageUrl}
          alt={props.makeLabel ?? ''}
          className="size-12 object-contain shrink-0"
        />
      ) : null}
      <VStack>
        {hasMakeLabel ? (
          <span className="text-sm leading-5 font-normal text-(--color-text-secondary)">
            {props.makeLabel}
          </span>
        ) : null}
        {hasModelTitle ? (
          <span className="text-xl leading-7 font-semibold text-(--color-text-primary) truncate">
            {props.modelTitle}
          </span>
        ) : null}
      </VStack>
    </HStack>
  );
}
